
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { getRoleOptions } from './EditMemberForm';
import { refreshTokenIfNeeded } from './tokenUtils';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
  SortingState,
} from '@tanstack/react-table';

export interface Member {
  id: number;
  name: string;
  email: string;
  nfc_token_id?: string;
  credits: number;
  securityRoles: string[];
  login_allowed?: boolean;
  sports?: { id: number; name: string }[];
  date_of_birth?: number;  // timestamp (ms)
  gender?: string;
  join_date?: number;      // timestamp (ms)
  membership_type?: string;
  membership_status?: string;
}

interface AdminTableProps {
  members: Member[];
  onEditMember: (member: Member) => void;
  onDeleteMember: (id: number) => void;
  loggedInUserId?: number;
  userRoles?: string[];
  accessToken?: string | null;
}


const columnHelper = createColumnHelper<Member>();

function hasRole(roles: string[] | undefined, role: string): boolean {
  if (!roles || !Array.isArray(roles)) return false;
  return roles.map(r => r.toLowerCase()).includes(role.toLowerCase());
}

function formatDate(value: unknown): string {
  if (!value) return '';
  // Handle Date objects and ISO strings: "2026-06-10T00:00:00.000Z" or "2026-06-10"
  if (value instanceof Date) {
    const y = value.getUTCFullYear();
    const m = String(value.getUTCMonth() + 1).padStart(2, '0');
    const d = String(value.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  const s = String(value);
  // Handle timestamp numbers from backend (may be negative for pre-1970 dates): convert to YYYY-MM-DD (UTC)
  if (/^-?\d+$/.test(s)) {
    const date = new Date(Number(s));
    const y = date.getUTCFullYear();
    const m = String(date.getUTCMonth() + 1).padStart(2, '0');
    const d = String(date.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  // ISO string from DATE column: take just the YYYY-MM-DD part
  if (s.includes('T')) return s.slice(0, 10);
  return s.length > 10 ? s.slice(0, 10) : s;
}

// Get the sports this user has access to (sports_admin roles)
function getUserSports(roles: string[] | undefined): string[] {
  if (!roles || !Array.isArray(roles)) return [];
  return roles
    .filter(r => typeof r === 'string' && r.startsWith('sports_admin_'))
    .map(r => r.replace('sports_admin_', '').toLowerCase());
}

// Check if user can manage this member
function canManageMember(userRoles: string[] | undefined, member: Member): boolean {
  if (!userRoles || !Array.isArray(userRoles)) return false;
  const roles = userRoles.map(r => r.toLowerCase());

  // Admin: can manage all members (takes precedence over trustee)
  if (roles.includes('admin')) return true;

  // Trustees cannot manage members
  if (roles.includes('trustee')) return false;

  // Sports admins: check if member is in any of their sports
  const userSports = getUserSports(userRoles);
  if (userSports.length > 0) {
    const memberSports = (member.sports || []).map(s => s.name.toLowerCase());
    return memberSports.some(ms => userSports.some(us => us === ms));
  }

  return false;
}

function getColumns(userRoles?: string[]) {
  // Admin: show all columns (takes precedence over trustee/sports)
  if (userRoles && hasRole(userRoles, 'admin')) {
    return [
      columnHelper.accessor('id', { header: 'ID', cell: info => info.getValue(), enableSorting: true }),
      columnHelper.accessor('name', { header: 'Name', cell: info => info.getValue(), enableSorting: true }),
      columnHelper.accessor('email', { header: 'Email', cell: info => info.getValue(), enableSorting: true }),
      columnHelper.accessor('nfc_token_id', { header: 'NFC Token ID', cell: info => info.getValue() || 'N/A', enableSorting: true }),
      columnHelper.accessor('date_of_birth', { header: 'Date of Birth', cell: info => formatDate(info.getValue()), enableSorting: true }),
      columnHelper.accessor('gender', { header: 'Gender', cell: info => info.getValue() || '', enableSorting: true }),
      columnHelper.accessor('join_date', { header: 'Join Date', cell: info => formatDate(info.getValue()), enableSorting: true }),
      columnHelper.accessor('membership_type', { header: 'Membership Type', cell: info => info.getValue() || '', enableSorting: true }),
      columnHelper.accessor('membership_status', { header: 'Membership Status', cell: info => info.getValue() || '', enableSorting: true }),
      columnHelper.accessor('credits', { header: 'Credits', cell: info => info.getValue(), enableSorting: true }),
      columnHelper.accessor('sports', { header: 'Sports', cell: info => info.row.original.sports && info.row.original.sports.length > 0 ? info.row.original.sports.map(s => s.name).join(', ') : 'None', enableSorting: true, sortingFn: (rowA, rowB, colId) => { const a = rowA.original.sports && rowA.original.sports.length > 0 ? rowA.original.sports.map(s => s.name).join(', ') : ''; const b = rowB.original.sports && rowB.original.sports.length > 0 ? rowB.original.sports.map(s => s.name).join(', ') : ''; return a.localeCompare(b); }, }),
      columnHelper.accessor('securityRoles', {
        header: 'Roles',
        cell: info => {
          const roles = info.getValue();
          if (!Array.isArray(roles) || roles.length === 0) return '';
          return roles.join(', ');
        },
      }),
      columnHelper.accessor('login_allowed', {
        header: 'Allow Login',
        cell: info => info.getValue() ? 'Yes' : 'No',
      }),
    ];
  }
  // Trustee: only show limited columns
  if (hasRole(userRoles, 'trustee')) {
    return [
      columnHelper.accessor('id', { header: 'ID', cell: info => info.getValue(), enableSorting: true }),
      columnHelper.accessor('name', { header: 'Name', cell: info => info.getValue(), enableSorting: true }),
      columnHelper.accessor('email', { header: 'Email', cell: info => info.getValue(), enableSorting: true }),
      columnHelper.accessor('membership_status', { header: 'Membership Status', cell: info => info.getValue() || '', enableSorting: true }),
    ];
  }
  // Sports Admin: show all columns except admin
  if (userRoles && userRoles.some(r => r.toLowerCase().startsWith('sports_admin'))) {
    return [
      columnHelper.accessor('id', { header: 'ID', cell: info => info.getValue(), enableSorting: true }),
      columnHelper.accessor('name', { header: 'Name', cell: info => info.getValue(), enableSorting: true }),
      columnHelper.accessor('email', { header: 'Email', cell: info => info.getValue(), enableSorting: true }),
      columnHelper.accessor('nfc_token_id', { header: 'NFC Token ID', cell: info => info.getValue() || 'N/A', enableSorting: true }),
      columnHelper.accessor('date_of_birth', { header: 'Date of Birth', cell: info => formatDate(info.getValue()), enableSorting: true }),
      columnHelper.accessor('gender', { header: 'Gender', cell: info => info.getValue() || '', enableSorting: true }),
      columnHelper.accessor('join_date', { header: 'Join Date', cell: info => formatDate(info.getValue()), enableSorting: true }),
      columnHelper.accessor('membership_type', { header: 'Membership Type', cell: info => info.getValue() || '', enableSorting: true }),
      columnHelper.accessor('membership_status', { header: 'Membership Status', cell: info => info.getValue() || '', enableSorting: true }),
      columnHelper.accessor('credits', { header: 'Credits', cell: info => info.getValue(), enableSorting: true }),
      columnHelper.accessor('sports', { header: 'Sports', cell: info => info.row.original.sports && info.row.original.sports.length > 0 ? info.row.original.sports.map(s => s.name).join(', ') : 'None', enableSorting: true, sortingFn: (rowA, rowB, colId) => { const a = rowA.original.sports && rowA.original.sports.length > 0 ? rowA.original.sports.map(s => s.name).join(', ') : ''; const b = rowB.original.sports && rowB.original.sports.length > 0 ? rowB.original.sports.map(s => s.name).join(', ') : ''; return a.localeCompare(b); }, }),
    ];
  }
  // Admin (fallback): show all columns
  return [
    columnHelper.accessor('id', { header: 'ID', cell: info => info.getValue(), enableSorting: true }),
    columnHelper.accessor('name', { header: 'Name', cell: info => info.getValue(), enableSorting: true }),
    columnHelper.accessor('email', { header: 'Email', cell: info => info.getValue(), enableSorting: true }),
    columnHelper.accessor('nfc_token_id', { header: 'NFC Token ID', cell: info => info.getValue() || 'N/A', enableSorting: true }),
    columnHelper.accessor('date_of_birth', { header: 'Date of Birth', cell: info => formatDate(info.getValue()), enableSorting: true }),
    columnHelper.accessor('gender', { header: 'Gender', cell: info => info.getValue() || '', enableSorting: true }),
    columnHelper.accessor('join_date', { header: 'Join Date', cell: info => formatDate(info.getValue()), enableSorting: true }),
    columnHelper.accessor('membership_type', { header: 'Membership Type', cell: info => info.getValue() || '', enableSorting: true }),
    columnHelper.accessor('membership_status', { header: 'Membership Status', cell: info => info.getValue() || '', enableSorting: true }),
    columnHelper.accessor('credits', { header: 'Credits', cell: info => info.getValue(), enableSorting: true }),
    columnHelper.accessor('sports', { header: 'Sports', cell: info => info.row.original.sports && info.row.original.sports.length > 0 ? info.row.original.sports.map(s => s.name).join(', ') : 'None', enableSorting: true, sortingFn: (rowA, rowB, colId) => { const a = rowA.original.sports && rowA.original.sports.length > 0 ? rowA.original.sports.map(s => s.name).join(', ') : ''; const b = rowB.original.sports && rowB.original.sports.length > 0 ? rowB.original.sports.map(s => s.name).join(', ') : ''; return a.localeCompare(b); }, }),
    columnHelper.accessor('securityRoles', {
      header: 'Roles',
      cell: info => {
        const roles = info.getValue();
        if (!Array.isArray(roles) || roles.length === 0) return '';
        const sports = info.row.original.sports || [];
        const options = getRoleOptions(sports);
        const map = Object.fromEntries(options.map(opt => [opt.value, opt.label]));
        return roles.map(r => map[r] || r).join(', ');
      },
      enableSorting: true
    }),
    columnHelper.accessor('login_allowed', {
      header: 'Allow Login',
      cell: info => info.getValue() ? 'Yes' : 'No',
    }),
  ];
}

const AdminTable: React.FC<AdminTableProps> = ({ members, onEditMember, onDeleteMember, loggedInUserId, userRoles, accessToken }) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const columns = getColumns(userRoles);
  const table = useReactTable({
    data: members,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: false,
  });

  // State for password reset dialog
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetMemberId, setResetMemberId] = useState<number | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetSuccess, setResetSuccess] = useState(false);

  const handleOpenResetDialog = (memberId: number) => {
    setResetMemberId(memberId);
    setShowResetDialog(true);
    setNewPassword('');
    setConfirmPassword('');
    setResetError(null);
    setResetSuccess(false);
  };

  const handleCloseResetDialog = () => {
    setShowResetDialog(false);
    setResetMemberId(null);
    setNewPassword('');
    setConfirmPassword('');
    setResetError(null);
    setResetSuccess(false);
  };

  const handleResetPassword = async () => {
    if (!newPassword || !confirmPassword || newPassword !== confirmPassword || !resetMemberId) {
      setResetError('Passwords do not match.');
      return;
    }
    setResetLoading(true);
    setResetError(null);
    setResetSuccess(false);
    try {
      // Refresh token if it's expiring soon
      const token = await refreshTokenIfNeeded(accessToken || null);
      const response = await fetch(`/api/members/${resetMemberId}/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ new_password: newPassword })
      });
      if (!response.ok) {
        throw new Error('Failed to reset password');
      }
      setResetSuccess(true);
      setTimeout(() => handleCloseResetDialog(), 1200);
    } catch (err: any) {
      setResetError(err.message || 'Unknown error');
    } finally {
      setResetLoading(false);
    }
  };

  if (members.length === 0) {
    return <p className="text-gray-600">No members found. Add one to get started!</p>;
  }
  return (
    <div className="overflow-x-auto rounded-lg shadow-md">
      <table className="min-w-full bg-white border border-gray-200">
        <thead className="bg-gray-200">
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <th
                  key={header.id}
                  className="py-3 px-4 text-left text-sm font-semibold text-gray-700 border-b cursor-pointer select-none"
                  onClick={header.column.getCanSort() ? header.column.getToggleSortingHandler() : undefined}
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                  {header.column.getIsSorted() ? (
                    <span className="ml-1">{header.column.getIsSorted() === 'asc' ? '▲' : '▼'}</span>
                  ) : null}
                </th>
              ))}
              <th className="py-3 px-4 text-left text-sm font-semibold text-gray-700 border-b">Actions</th>
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map(row => (
            <tr key={row.id} className="hover:bg-gray-50">
              {row.getVisibleCells().map(cell => (
                <td key={cell.id} className="py-3 px-4 border-b border-gray-200 text-sm text-gray-800">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
              <td className="py-3 px-4 border-b border-gray-200 text-sm">
                {canManageMember(userRoles, row.original) && (
                  <button
                    onClick={() => onEditMember(row.original)}
                    className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-3 rounded-md mr-2 shadow-sm transition duration-300 ease-in-out"
                  >
                    Edit
                  </button>
                )}
                {loggedInUserId !== row.original.id && canManageMember(userRoles, row.original) && (
                  <button
                    onClick={() => onDeleteMember(row.original.id)}
                    className="bg-red-500 hover:bg-red-700 text-white font-bold py-1 px-3 rounded-md shadow-sm transition duration-300 ease-in-out mr-2"
                  >
                    Delete
                  </button>
                )}
                {/* Reset Password button: visible to admin, trustee, sports admin, or self — only if member has login enabled */}
                {row.original.login_allowed && (hasRole(userRoles, 'admin') ||
                  hasRole(userRoles, 'trustee') ||
                  loggedInUserId === row.original.id ||
                  canManageMember(userRoles, row.original)) && (
                  <button
                    onClick={() => handleOpenResetDialog(row.original.id)}
                    className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold py-1 px-3 rounded-md shadow-sm transition duration-300 ease-in-out"
                    style={{ marginLeft: 4 }}
                  >
                    Reset Password
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Password Reset Dialog */}
      {showResetDialog && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-sm">
            <h2 className="text-lg font-semibold mb-4">Reset Password</h2>
            <label className="block mb-2 text-sm font-medium text-gray-700">New Password</label>
            <input
              type="password"
              className="w-full px-3 py-2 border rounded-md mb-3 focus:outline-none focus:ring focus:border-blue-300"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              disabled={resetLoading}
            />
            <label className="block mb-2 text-sm font-medium text-gray-700">Confirm Password</label>
            <input
              type="password"
              className="w-full px-3 py-2 border rounded-md mb-3 focus:outline-none focus:ring focus:border-blue-300"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              disabled={resetLoading}
            />
            {newPassword && confirmPassword && newPassword !== confirmPassword && (
              <div className="text-red-500 text-sm mb-2">Passwords do not match.</div>
            )}
            {resetError && <div className="text-red-500 text-sm mb-2">{resetError}</div>}
            {resetSuccess && <div className="text-green-500 text-sm mb-2">Password reset successfully!</div>}
            <div className="flex justify-end space-x-2 mt-4">
              <button
                onClick={handleCloseResetDialog}
                className="px-4 py-2 rounded-md bg-gray-300 text-gray-700 hover:bg-gray-400"
                disabled={resetLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleResetPassword}
                className="px-4 py-2 rounded-md bg-yellow-500 text-white font-bold hover:bg-yellow-600"
                disabled={resetLoading || !newPassword || !confirmPassword || newPassword !== confirmPassword}
              >
                {resetLoading ? 'Resetting...' : 'Reset Password'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTable;
